<template>
  <div v-if="showing" class="demo-banner-wrap">
    <div class="demo-banner" :class="{ 'demo-banner--ending': ending }">
      <span class="demo-banner__dot" aria-hidden="true"></span>
      <p class="demo-banner__text">
        {{ t('demo.banner.text') }}
        <span v-if="sectorLabel" class="demo-banner__sector">{{ t('demo.banner.sector', { sector: sectorLabel }) }}</span>
      </p>
      <span v-if="left !== null" class="demo-banner__clock" role="timer" aria-live="polite">
        {{ ending ? t('demo.banner.ending', { minutes: left }) : t('demo.banner.remaining', { minutes: left }) }}
      </span>
      <a v-if="visit.contactUrl" class="demo-banner__link" :href="visit.contactUrl" target="_blank" rel="noopener">
        {{ t('demo.banner.contact') }}
      </a>
      <a v-if="frontPageUrl" class="demo-banner__link" :href="frontPageUrl">
        {{ t('demo.banner.change') }}
      </a>
      <RouterLink v-else-if="visit.directEntry !== false" class="demo-banner__link" to="/demo">
        {{ t('demo.banner.change') }}
      </RouterLink>
    </div>

    <!--
      What a visitor sees when they press Save. Not an error: the form worked,
      the demo simply does not keep anything — and that is the moment somebody
      is most ready to hear how to get an instance that does.
    -->
    <div v-if="readOnlyNotice" class="demo-notice" role="status" aria-live="polite">
      <div class="demo-notice__body">
        <p class="demo-notice__title">{{ t('demo.banner.readOnly.title') }}</p>
        <p class="demo-notice__text">{{ t('demo.banner.readOnly.text') }}</p>
      </div>
      <a v-if="visit.contactUrl" class="demo-notice__cta" :href="visit.contactUrl" target="_blank" rel="noopener">
        {{ t('demo.banner.readOnly.cta') }}
      </a>
      <button type="button" class="demo-notice__close" @click="readOnlyNotice = false">
        {{ t('demo.banner.readOnly.close') }}
      </button>
    </div>
  </div>
</template>

<script setup>
// ── The band that says this is not your data ──────────────────────────────────
//
// One line, and not optional. Everything under it is a working ERP full of
// invented companies, and the visit that matters most — somebody opening the
// link again a week after the meeting (DEMOS.md §8) — is the one where nobody
// is sitting beside them to explain that.
//
// On the public demo it also carries the visit: how long is left (and a warning
// in the last five minutes), which industry's data this is, and the way to ask
// for an instance. It is where the read-only notice appears, because it is the
// one piece of the demo present on every screen; and it counts which modules
// the visitor opens, for the same reason.

import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink, useRoute } from 'vue-router'
import { DEMO_READ_ONLY_EVENT } from '@general/ui-kit'

import { demoApi } from '../api'
import { isDemoInstance, demoVisit, leaveDemo } from '../session'
import { minutesLeft, moduleOfRoute, frontPage, WARN_MINUTES } from '../visit'

const { t } = useI18n()
const route = useRoute()

// Read once rather than reactively: an instance does not stop being a demo
// while somebody is looking at it.
const showing = computed(() => isDemoInstance())
const visit = ref(demoVisit())

const sectorLabel = computed(() => visit.value.sector?.label || '')
const frontPageUrl = computed(() => (visit.value.directEntry === false ? frontPage(visit.value.exitUrl) : ''))

// ── The clock ────────────────────────────────────────────────────────────────
const now = ref(Date.now())
const left = computed(() => minutesLeft(visit.value.endsAt, now.value))
const ending = computed(() => left.value !== null && left.value <= WARN_MINUTES)
let timer = null

function tick() {
  now.value = Date.now()
  // The server ends the visit anyway; this only saves the visitor a click on
  // something that would answer 401.
  if (left.value === 0) leaveDemo()
}

// ── The read-only notice ─────────────────────────────────────────────────────
const readOnlyNotice = ref(false)
const onReadOnly = () => { readOnlyNotice.value = true }

// ── What they opened ─────────────────────────────────────────────────────────
let lastModule = null
watch(() => route.fullPath, () => {
  if (!showing.value) return
  const module = moduleOfRoute(route)
  if (!module || module === lastModule) return
  lastModule = module
  demoApi.track(`${module}:open`).catch(() => {})
}, { immediate: true })

onMounted(() => {
  if (!showing.value) return
  visit.value = demoVisit()
  timer = setInterval(tick, 15_000)
  tick()
  window.addEventListener(DEMO_READ_ONLY_EVENT, onReadOnly)
})

onBeforeUnmount(() => {
  if (timer) clearInterval(timer)
  window.removeEventListener(DEMO_READ_ONLY_EVENT, onReadOnly)
})
</script>

<style scoped>
.demo-banner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2) var(--space-3);
  padding: var(--space-2) var(--space-4);
  background: var(--color-warning-soft);
  color: var(--color-warning);
  border-bottom: 1px solid var(--color-border);
  font-size: var(--text-sm);
}

.demo-banner__dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: var(--radius-full);
  background: currentColor;
  flex: none;
}

.demo-banner__text {
  margin: 0;
  flex: 1 1 16rem;
  min-width: 0;
}

.demo-banner__sector {
  font-weight: 600;
  margin-left: var(--space-2);
}

.demo-banner__clock {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.demo-banner--ending .demo-banner__clock {
  font-weight: 700;
  color: var(--color-danger);
}

.demo-banner__link {
  color: inherit;
  font-weight: 600;
  text-decoration: underline;
  white-space: nowrap;
}

.demo-notice {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border);
  font-size: var(--text-sm);
}

.demo-notice__body {
  flex: 1 1 18rem;
  min-width: 0;
}

.demo-notice__title {
  margin: 0;
  font-weight: 700;
}

.demo-notice__text {
  margin: var(--space-1) 0 0;
  color: var(--color-text-soft);
}

.demo-notice__cta {
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-sm);
  background: var(--color-primary);
  color: var(--color-text-on-primary);
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
}

.demo-notice__close {
  background: none;
  border: none;
  color: inherit;
  text-decoration: underline;
  cursor: pointer;
  font: inherit;
}
</style>
